import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Stack,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  InputAdornment
} from '@mui/material';
import {
  Send as SendIcon,
  SwapHoriz as StatusChangeIcon,
  ChatBubbleOutline as NoteIcon
} from '@mui/icons-material';
import {
  getApplicationNotes,
  addApplicationNote,
  ApplicationNote
} from '../services/applicationService';

interface NotesChatProps {
  applicationId: string;
  entityId: string;
  currentUserId: string;
  currentUserName: string;
  currentUserRole: string;
  maxHeight?: number;
}

export const NotesChat: React.FC<NotesChatProps> = ({
  applicationId,
  entityId,
  currentUserId,
  currentUserName,
  currentUserRole,
  maxHeight = 400
}) => {
  const theme = useTheme();
  const [notes, setNotes] = useState<ApplicationNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  useEffect(() => {
    scrollToBottom();
  }, [notes]);

  const loadNotes = async () => {
    setLoading(true);
    const data = await getApplicationNotes(applicationId);
    setNotes(data);
    setLoading(false);
  };

  const handleSendNote = async () => {
    const content = newNote.trim();
    if (!content || sending) return;

    // Optimistic UI: add note immediately
    const optimisticNote: ApplicationNote = {
      id: `temp-${Date.now()}`,
      application_id: applicationId,
      entity_id: entityId,
      author_id: currentUserId,
      author_name: currentUserName,
      author_role: currentUserRole,
      content,
      note_type: 'comment',
      metadata: {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    setNotes(prev => [...prev, optimisticNote]);
    setNewNote('');
    setSending(true);

    const result = await addApplicationNote({
      applicationId,
      entityId,
      authorId: currentUserId,
      authorName: currentUserName,
      authorRole: currentUserRole,
      content,
      noteType: 'comment'
    });

    if (result) {
      // Replace optimistic note with real one
      setNotes(prev => prev.map(n => n.id === optimisticNote.id ? result : n));
    } else {
      // Remove optimistic note on failure
      setNotes(prev => prev.filter(n => n.id !== optimisticNote.id));
      setNewNote(content); // Restore the text
    }

    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendNote();
    }
  };

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  const getRoleLabel = (role: string | null) => {
    if (!role) return '';
    const labels: Record<string, string> = {
      admin: 'Admin',
      hr: 'HR',
      manager: 'Manager',
      recruiter: 'Recruiter',
      super_admin: 'Super Admin',
      system: 'System'
    };
    return labels[role] || role;
  };

  const getRoleColor = (role: string | null) => {
    switch (role) {
      case 'admin': return theme.palette.error.main;
      case 'hr': return theme.palette.primary.main;
      case 'manager': return theme.palette.warning.main;
      case 'recruiter': return theme.palette.info.main;
      case 'super_admin': return theme.palette.secondary.main;
      default: return theme.palette.grey[500];
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <NoteIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={600}>
          Notes & Activity
        </Typography>
        <Chip label={notes.length} size="small" variant="outlined" />
      </Stack>

      {/* Messages Area */}
      <Box
        sx={{
          maxHeight,
          overflowY: 'auto',
          mb: 2,
          p: 2,
          bgcolor: alpha(theme.palette.grey[500], 0.04),
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`
        }}
      >
        {notes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <NoteIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notes yet. Add the first note below.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {notes.map((note) => {
              const isCurrentUser = note.author_id === currentUserId;
              const isStatusChange = note.note_type === 'status_change';
              const isSystem = note.note_type === 'system';

              // Status change notes - centered system style
              if (isStatusChange || isSystem) {
                return (
                  <Box
                    key={note.id}
                    sx={{
                      textAlign: 'center',
                      py: 1,
                      px: 2
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                      <StatusChangeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        <strong>{note.author_name}</strong> {note.content}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                      {formatTimestamp(note.created_at)}
                    </Typography>
                  </Box>
                );
              }

              // Regular comment notes - alternating chat bubbles
              return (
                <Box
                  key={note.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '80%',
                      bgcolor: isCurrentUser
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.grey[500], 0.08),
                      p: 1.5,
                      borderRadius: 2,
                      ...(isCurrentUser
                        ? {
                            borderTopRightRadius: 4,
                            borderBottomRightRadius: 0,
                            borderRight: `3px solid ${theme.palette.primary.main}`,
                          }
                        : {
                            borderTopLeftRadius: 4,
                            borderBottomLeftRadius: 0,
                            borderLeft: `3px solid ${getRoleColor(note.author_role)}`,
                          }),
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      {!isCurrentUser && (
                        <Typography variant="caption" fontWeight={700} color="text.primary">
                          {note.author_name}
                        </Typography>
                      )}
                      {isCurrentUser && (
                        <Typography variant="caption" fontWeight={700} color="primary.main">
                          You
                        </Typography>
                      )}
                      {note.author_role && (
                        <Chip
                          label={getRoleLabel(note.author_role)}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: alpha(getRoleColor(note.author_role), 0.1),
                            color: getRoleColor(note.author_role)
                          }}
                        />
                      )}
                      <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto !important' }}>
                        {formatTimestamp(note.created_at)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {note.content}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Stack>
        )}
      </Box>

      {/* Input Area */}
      <TextField
        fullWidth
        size="small"
        placeholder="Add a note..."
        value={newNote}
        onChange={(e) => setNewNote(e.target.value)}
        onKeyDown={handleKeyDown}
        multiline
        maxRows={3}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleSendNote}
                disabled={!newNote.trim() || sending}
                color="primary"
                size="small"
              >
                {sending ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2
          }
        }}
      />
    </Box>
  );
};
